/**
 * metaLeadGen/unifiedFunnelService.ts
 *
 * Unified Paid Meta Funnel service.
 *
 * Bridges two isolated data modules:
 *   1. metaLeadGen  — spend, impressions, clicks, leads, CPL
 *   2. crmFunnel    — MQLs, meetings, deals, revenue (Close CRM, currently mock)
 *
 * ARCHITECTURE CONTRACT:
 *   - Never import UI. Never calculate metrics — only assemble and derive from
 *     existing module outputs using safeDivide.
 *   - crmFunnel campaign/adset/ad filter mapping is NOT implemented yet
 *     (ID spaces differ between Meta and Close CRM). Only dateRange is bridged.
 *   - When Close CRM live data is connected, replace RAW_CRM_FUNNEL_ROWS with
 *     adaptCloseCrmResponse() — no changes needed here.
 */

import type { MetaLeadGenRow } from "./types";
import type { ActiveFilters } from "./useFilters";
import { getMetaLeadGenComparison } from "./service";
import { getCrmFunnelData } from "../crmFunnel/service";
import { RAW_CRM_FUNNEL_ROWS } from "../crmFunnel/mockData";
import type { CrmFunnelFilters, CrmFunnelMetrics } from "../crmFunnel/types";
import { safeDivide } from "./calculations";

// ─────────────────────────────────────────────
// OUTPUT TYPES
// ─────────────────────────────────────────────

/**
 * Full unified funnel snapshot for a single period.
 *
 * Spend + Leads + CPL come from metaLeadGen.
 * MQL+ funnel stages come from crmFunnel.
 * Cross-source rates (leadQualificationRate, etc.) use Meta's leads as base.
 * Cost metrics use Meta's spend as numerator.
 */
export interface UnifiedFunnelData {
  // ── Spend (metaLeadGen) ──────────────────────────────────────────────────
  spend: number;

  // ── Funnel stages ────────────────────────────────────────────────────────
  leads: number;            // opt-ins from metaLeadGen (Meta "Lead" events)
  crmLeads: number;         // applications from crmFunnel (people who entered CRM)
  mqls: number;             // from crmFunnel
  meetingsBooked: number;   // from crmFunnel
  meetingsAttended: number; // from crmFunnel
  closedWonDeals: number;   // from crmFunnel

  // ── Revenue (crmFunnel) ──────────────────────────────────────────────────
  revenue: number;

  // ── Stage conversion rates ───────────────────────────────────────────────
  applicationRate: number;       // crmLeads / leads * 100  (opt-in → application)
  leadQualificationRate: number; // mqls / crmLeads * 100   (application → MQL)
  bookingRate: number;           // meetingsBooked / mqls * 100
  showRate: number;              // meetingsAttended / meetingsBooked * 100
  closeRate: number;             // closedWonDeals / meetingsAttended * 100

  // ── Cost efficiency (spend from metaLeadGen, stages from crmFunnel) ──────
  cpl: number;                // spend / leads           (from metaLeadGen)
  costPerMql: number;         // spend / mqls
  costPerBooking: number;     // spend / meetingsBooked
  costPerOpportunity: number; // spend / meetingsAttended
  cpa: number;                // spend / closedWonDeals

  // ── Revenue efficiency ───────────────────────────────────────────────────
  roas: number;                  // revenue / spend
  revenuePerLead: number;        // revenue / leads
  revenuePerOpportunity: number; // revenue / closedWonDeals
}

export interface UnifiedFunnelComparison {
  current: UnifiedFunnelData;
  previous: UnifiedFunnelData;
  /** % change per metric. Positive = improved regardless of direction. */
  changes: Record<keyof UnifiedFunnelData, number>;
}

// ─────────────────────────────────────────────
// INTERNAL: ASSEMBLE ONE PERIOD
// ─────────────────────────────────────────────

function assemblePeriod(
  spend: number,
  leads: number,
  cpl: number,
  crm: CrmFunnelMetrics
): UnifiedFunnelData {
  const { leads: crmLeads, mqls, meetingsBooked, meetingsAttended, closedWonDeals, revenue } = crm;

  return {
    spend,
    leads,
    crmLeads,
    mqls,
    meetingsBooked,
    meetingsAttended,
    closedWonDeals,
    revenue,

    applicationRate:       safeDivide(crmLeads, leads) * 100,
    leadQualificationRate: safeDivide(mqls, crmLeads) * 100,
    bookingRate:           safeDivide(meetingsBooked, mqls) * 100,
    showRate:              safeDivide(meetingsAttended, meetingsBooked) * 100,
    closeRate:             safeDivide(closedWonDeals, meetingsAttended) * 100,

    cpl,
    costPerMql:         safeDivide(spend, mqls),
    costPerBooking:     safeDivide(spend, meetingsBooked),
    costPerOpportunity: safeDivide(spend, meetingsAttended),
    cpa:                safeDivide(spend, closedWonDeals),

    roas:                  safeDivide(revenue, spend),
    revenuePerLead:        safeDivide(revenue, leads),
    revenuePerOpportunity: safeDivide(revenue, closedWonDeals),
  };
}

function buildChanges(
  current: UnifiedFunnelData,
  previous: UnifiedFunnelData
): Record<keyof UnifiedFunnelData, number> {
  const keys = Object.keys(current) as Array<keyof UnifiedFunnelData>;
  const result = {} as Record<keyof UnifiedFunnelData, number>;
  for (const key of keys) {
    result[key] = previous[key] === 0 ? 0 : safeDivide(current[key] - previous[key], previous[key]) * 100;
  }
  return result;
}

// ─────────────────────────────────────────────
// PUBLIC API
// ─────────────────────────────────────────────

/**
 * Returns current-period and previous-period unified funnel metrics,
 * plus % changes for every KPI.
 *
 * @param filters  Active filter state from useMetaLeadGenFilters()
 */
export function getUnifiedFunnelData(
  filters: ActiveFilters,
  rows?: MetaLeadGenRow[]
): UnifiedFunnelComparison {
  // ── metaLeadGen: spend, leads, CPL ──────────────────────────────────────────
  const metaComp = getMetaLeadGenComparison(filters, rows);

  // ── crmFunnel: downstream stages ────────────────────────────────────────────
  // Bridge dateRange + campaign/adset/ad hierarchy filters.
  // Age, gender, device, creative type are Meta impression-level attributes
  // and do not exist on CRM lead rows — those filters are intentionally not bridged.
  const crmFilters: CrmFunnelFilters = {
    dateRange: filters.dateRange,
    ...(filters.campaignIds?.length ? { metaCampaignIds: filters.campaignIds } : {}),
    ...(filters.adsetIds?.length    ? { metaAdsetIds:    filters.adsetIds    } : {}),
    ...(filters.adIds?.length       ? { metaAdIds:       filters.adIds       } : {}),
  };
  const crmComp = getCrmFunnelData(RAW_CRM_FUNNEL_ROWS, crmFilters);

  // ── Assemble both periods ────────────────────────────────────────────────────
  const current = assemblePeriod(
    metaComp.current.spend,
    metaComp.current.leads,
    metaComp.current.cpl,
    crmComp.current
  );

  const previous = assemblePeriod(
    metaComp.previous.spend,
    metaComp.previous.leads,
    metaComp.previous.cpl,
    crmComp.previous
  );

  return { current, previous, changes: buildChanges(current, previous) };
}
